import { motion, useReducedMotion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import { cn } from '@/lib/utils'

type Direction = 'row' | 'column'

interface VenuePortalProps {
  direction?: Direction
  className?: string
}

const venues = [
  {
    key: 'maia' as const,
    routeKey: 'maia' as const,
    imageSrc: '/media/maia.jpg',
    nameKey: 'venues.maia.name' as const,
    labelKey: 'hero.bubbleMaia' as const,
    bg: 'bg-maia',
    text: 'text-white',
    focus: 'focus-visible:ring-white',
  },
  {
    key: 'bar' as const,
    routeKey: 'bar' as const,
    imageSrc: '/media/bar.jpg',
    nameKey: 'venues.bar.name' as const,
    labelKey: 'hero.bubbleBar' as const,
    bg: 'bg-bar',
    text: 'text-ink',
    focus: 'focus-visible:ring-ink',
  },
  {
    key: 'pizza' as const,
    routeKey: 'pizza' as const,
    imageSrc: '/media/pizza.jpg',
    nameKey: 'venues.pizza.name' as const,
    labelKey: 'hero.bubblePizza' as const,
    bg: 'bg-pizza',
    text: 'text-white',
    focus: 'focus-visible:ring-white',
  },
]

export function VenuePortal({ direction = 'column', className }: VenuePortalProps) {
  const { t } = useTranslation('home')
  const shouldReduceMotion = useReducedMotion()
  const isRow = direction === 'row'
  const ease = [0.22, 1, 0.36, 1] as const

  return (
    <nav
      aria-label={t('hero.portalLabel')}
      className={cn(
        'flex h-full w-full overflow-hidden',
        isRow ? 'flex-col lg:flex-row' : 'flex-col',
        className
      )}
    >
      {venues.map((venue, index) => {
        const link = (
          <LocalizedLink
            to={venue.routeKey}
            className={cn(
              'group relative flex h-full w-full overflow-hidden',
              venue.bg,
              venue.text,
              'focus-visible:outline-none',
              venue.focus,
              'focus-visible:ring-4 focus-visible:ring-offset-0'
            )}
            aria-label={t(venue.nameKey)}
          >
            {/* Background image */}
            <img
              src={venue.imageSrc}
              alt=""
              className={cn(
                'absolute inset-0 h-full w-full object-cover transition-all duration-700',
                'opacity-30 grayscale-[20%] group-hover:opacity-60 group-hover:grayscale-0 group-hover:scale-105',
                venue.key === 'bar' ? 'brightness-95' : 'brightness-85'
              )}
            />
            {/* Overlay gradient for text legibility */}
            <div
              className={cn(
                'absolute inset-0 transition-opacity duration-500',
                venue.key === 'bar'
                  ? 'bg-gradient-to-t from-bar-dark/40 via-transparent to-transparent'
                  : 'bg-gradient-to-t from-black/40 via-transparent to-transparent'
              )}
            />
            {/* Content */}
            <div className="relative z-10 flex h-full w-full flex-col justify-end p-6 sm:p-8 lg:p-10">
              <h3 className="font-display text-3xl font-extrabold leading-none tracking-tight sm:text-4xl lg:text-[clamp(2rem,4vw,3.5rem)]">
                {t(venue.nameKey)}
              </h3>
              <span
                className={cn(
                  'mt-4 inline-flex items-center gap-1 text-sm font-semibold transition-transform duration-300 group-hover:translate-x-1',
                  venue.key === 'bar' ? 'text-ink/80' : 'text-white/80'
                )}
              >
                {t('hero.ctaDiscover')}
                <span aria-hidden="true">→</span>
              </span>
            </div>
          </LocalizedLink>
        )

        if (shouldReduceMotion) {
          return (
            <div
              key={venue.key}
              className="relative flex flex-1 overflow-hidden transition-all duration-500 ease-out hover:flex-[1.35] focus-within:flex-[1.35]"
            >
              {link}
            </div>
          )
        }

        return (
          <motion.div
            key={venue.key}
            initial={{ opacity: 0, y: isRow ? 0 : 32, x: isRow ? -32 : 0 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 + index * 0.12, ease }}
            className="relative flex flex-1 overflow-hidden transition-all duration-500 ease-out hover:flex-[1.35] focus-within:flex-[1.35]"
          >
            {link}
          </motion.div>
        )
      })}
    </nav>
  )
}
